import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User as UserIcon } from 'lucide-react';
import { type WhoElseDoesThisItem } from '@bliks-io/contracts';
import { getEntityRefId } from '@/lib/entityRefUtils';
import { usePrefetchPickerSuggestions } from '@/api/queries/usePickerSuggestions';
import { EntityPickerCell } from '@/components/entity-pickers/preview/EntityPickerCell';
import { EntityPickerList } from '@/components/entity-pickers/preview/EntityPickerList';
import { usePersonPreviewItems } from '@/components/entity-pickers/preview/useEntityPreviewItems';
import { PersonPickerModal } from '@/components/entity-pickers';

export interface WhoElseDoesThisPickerProps {
  value: WhoElseDoesThisItem[];
  onChange: (value: WhoElseDoesThisItem[]) => void | Promise<void>;
  /** Person ids to hide from the picker (e.g. the current user and the assignee). */
  excludeIds?: string[];
  /** Process the picker opens from; drives the "for this process" bucket. */
  processId?: string;
  /** Current recording — excluded from the "for this process" bucket aggregate. */
  recordingId?: string;
}

const EMPTY_EXCLUDE: string[] = [];

/**
 * Shared "who else does this" picker: the colleague preview cell plus the
 * `PersonPickerModal`. Renders no surrounding chrome — callers wrap it with
 * their own header/container. Used by the recording flow's `WhoElseDoesThisCard`
 * and the editor's step-detail panels.
 *
 * Internalizes the User-id ↔ `WhoElseDoesThisItem` mapping: non-User entries
 * (Teams) and the existing acceptance status of kept users are preserved across
 * edits; newly added users default to `pending`.
 */
export function WhoElseDoesThisPicker({
  value,
  onChange,
  excludeIds = EMPTY_EXCLUDE,
  processId,
  recordingId
}: WhoElseDoesThisPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  usePrefetchPickerSuggestions(['user'], { processId, recordingId });

  // Users always carry an `entity` ref; map to raw ids for the picker.
  const userIds = useMemo(
    () =>
      value
        .filter((i) => i.model === 'User')
        .map((i) => getEntityRefId(i.entity))
        .filter((id): id is string => id !== undefined),
    [value]
  );

  const personPreview = usePersonPreviewItems(userIds);

  const handleConfirm = (newIds: string[]) => {
    const nonUserEntries = value.filter((i) => i.model !== 'User');
    const userEntries: WhoElseDoesThisItem[] = newIds.map(
      (id) =>
        value.find((i) => i.model === 'User' && getEntityRefId(i.entity) === id) ?? {
          entity: { type: 'entity', id },
          model: 'User',
          status: 'pending'
        }
    );
    void onChange([...nonUserEntries, ...userEntries]);
    setOpen(false);
  };

  return (
    <>
      <EntityPickerCell
        icon={UserIcon}
        label={t('viralRecording.helpersHeading.colleagues', {
          count: value.length
        })}
        onClick={() => setOpen(true)}
      >
        <EntityPickerList items={personPreview.items} loadingCount={personPreview.loadingCount} />
      </EntityPickerCell>
      {/* Always-mount so Radix can play the exit animation when open flips. */}
      <PersonPickerModal
        open={open}
        onOpenChange={setOpen}
        mode="multi"
        selectedIds={userIds}
        onConfirm={handleConfirm}
        excludeIds={excludeIds}
        processId={processId}
        recordingId={recordingId}
        copyOverrides={{
          title: t('processPage.whoElseDoesThis'),
          description: t('processPage.selectColleaguesWhoAlsoPerform')
        }}
      />
    </>
  );
}
